import { CreateCourseRequest, EditCourseRequest, SectionsIds } from "Components/CourseCatalog/CourseCatalog";
import { CourseSectionUnmapping, NewSection, SectionRequest } from "Components/CourseCatalog/CourseCatalogSubTable";
import { observable, makeObservable, flow, runInAction, action } from "mobx";
import { EAIClient } from "Services/EAIClient";
import { EAIError } from "Stores/appStore";

export type CourseStatus =
    | "active"
    | "processing"
    | "archived"
    | "disabled"
    | undefined;

export interface CoursesCatalogResponse {
    result: Result
    data: CoursesCatalogInterface[] | []
}

export interface SectionsCatalogResponse {
    result: Result,
    data: {
        total: number,
        total_filtered: number,
        data: SectionsCatalogInterface[]}
}


export interface Result {
    code: number
    message: string
}

export interface CoursesCatalogInterface {
    rid: number
    school_id: string
    course_title: string
    subject_number: string
    curriculum_year: string
    credit_hours: number
    data_source: string
    status: CourseStatus
    created: string
    updated: string
    sections?: SectionsCatalogInterface[]
    override?: Override
    cross_listed?: CoursesCatalogInterface[]
    curriculum_mapping?: CurriculumMapping[]
    last_term_id: number
    description: string
}

export interface CourseDescription {
    description: string;
    update_comment: string
}

export interface SuggestedCourseInterface {
    ruuid: string
    school_id: string
    enflux_course_id: number
    enflux_catalog_id: number
    rank: number
    reason: string
    similarity: number
    reverse_similarity: number
    enflux_created: string
    enflux_updated: string
    course_catalog: CoursesCatalogInterface
}

export interface SectionsCatalogInterface {
    id: number
    school_id: string
    name: string
    course_id: string
    term_id: number | string
    disabled: boolean
    needs_review: boolean
    course: string
    course_short_name: string
    course_title: string
    created: string
    updated: string
    subject_number: string
    curriculum_year: string
    credit_hours: number
    term_name: string
    term_order: number
    override: Override
}

export interface Override {
    changer: Changer
    comment: string
    date: string
}

export interface Changer {
    id: number;
    prefix: string;
    first_name: string;
    last_name: string;
    suffix: string;
    name: string;
    email: string;
}

export interface TeachingMethods {
    teaching_method: TeachingMethod;
}

export interface CurriculumMapping {
    ruuid: string;
    course_catalog_rid: string;
    curriculum_mapping_clos_ruuid?: string;
    domain_competency_ruuid: string;
    cognitive_level_ruuid?: string;
    teaching_method_ruuid?: string;
    status?: string;
    description?: string;
    mode?: "edit" | "create";
    curriculum_mapping_clos?: {
        ruuid: string;
        clo: string;
        status: string;
        school_id: string;
    };
    cognitive_levels?: CognitiveLevels[];
    teaching_methods?: Array<{
        teaching_method: {
            ruuid: string;
            status: string;
            school_id: string;
            editable: boolean;
            method_name: string;
        };
    }>;
    domain_competency?: {
        ruuid: string;
        school_id: string;
        editable: boolean;
        standard_name: string;
        standard_item: string;
        standard_level: number;
        enabled: boolean;
        domain_competency_parent_ruuid: string;
    }
}

export interface OptionType {
    name: string,
    status?: "archived" | "active",
    ruuid?: string,
    editable?: boolean,
}

export interface TeachingMethod {
    ruuid: string;
    school_id: string;
    method_name: string;
    status: "archived" | "active";
    curriculum_mapping_clos_ruuid: string;
    enflux_created: string;
    enflux_updated: string;
}
export interface CognitiveLevels {
    ruuid: string;
    status: string;
    school_id: string;
    cognitive_level_name: string;
    editable: boolean;
}

export interface CustomOptionType {
    name: string,
    status?: "archived" | "active",
    ruuid?: string,
    editable?: boolean,
    custom_category?: boolean,
    children?: OptionType[]
    teaching_method?: TeachingMethod
}

export interface DomainCompetency {
    ruuid: string;
    schoold_id: string;
    standard_name: string;
    standard_item: string;
    enflux_created: string;
    enflux_updated: string;
    standard_level: number;
    enabled: boolean;
    school_enabled?: boolean;
    domain_competency_parent_ruuid?: string;
    children: Array<DomainCompetency>;
}

export interface CustomDomainCompetency extends Omit<DomainCompetency, "ruuid"> {
    ruuid?: string;
}


export interface CustomOptionType {
    name: string,
    status?: "archived" | "active",
    ruuid?: string,
    editable?: boolean,
}

export class CoursesCatalogStore {

    client: EAIClient = new EAIClient();
    @observable coursesCatalog: CoursesCatalogInterface[];
    @observable suggestedCourses: SuggestedCourseInterface[];
    @observable unmappedSections: SectionsCatalogResponse;
    @observable selectedCourseCatalog: CoursesCatalogInterface;
    @observable selectedSecion: SectionsCatalogInterface;
    @observable cognitiveLevels: CustomOptionType[];
    @observable CLOs: CustomOptionType[];
    @observable domainCompetencies: DomainCompetency[];
    @observable teachingMethods: CustomOptionType[];
    @observable isLoadingCourses: boolean;
    @observable isSuccessCourses: boolean;
    @observable isSuccessMapping: boolean;
    @observable isLoadingCrossed: boolean;
    @observable isError: boolean;
    @observable error: EAIError;
    @observable isLoadingMapping: boolean;
    @observable isErrorDeleteLabel: boolean;
    @observable errorMessage = "";

    constructor() {
        makeObservable(this);
    }

    @action
    clearError() {
        this.isError = false;
        this.errorMessage = "";
    }

    @flow
    *fetchCoursesCatalog() {
        this.isLoadingCourses = true;
        try {
            const { data } = yield this.client.getCourseCatalog();
            runInAction(() => {
                this.coursesCatalog = data.data;
                this.isSuccessCourses = true;
            });
        } catch (error) {
            runInAction(() => {
                this.error = error;
                this.isError = true;
            });
        } finally {
            this.isLoadingCourses = false;

        }
    }

    @flow
    *updateCourseCatalog(edit: EditCourseRequest) {
        this.isLoadingCourses = true;
        try {
            const { data } = yield this.client.updateCourseCatalog(edit);
            this.isSuccessCourses = true;
            return data;
        } catch (error) {
            this.error = error;
            this.isError = true;
        } finally {
            this.isLoadingCourses = false;

        }
    }
    @flow
    *createCourse(newCourse: CreateCourseRequest) {
        this.isLoadingCourses = true;
        try {
            const { data } = yield this.client.createCourse(newCourse);
            this.isSuccessCourses = true;
            return data;
        } catch (error) {
            this.error = error;
            this.isError = true;
        } finally {
            this.isLoadingCourses = false;

        }
    }
    @flow
    *addCourseDescription(course_rid: string, description: CourseDescription) {
        this.isLoadingCourses = true;
        try {
            yield this.client.addCourseDescription(course_rid, description);
            this.isSuccessCourses = true;
        } catch (error) {
            this.error = error;
            this.isError = true;
        } finally {
            this.isLoadingCourses = false;
        }
    }
    @flow
    *archiveCouse(status: CourseStatus, last_term_id: number, update_comment: string, course_rid: string) {
        this.isLoadingCourses = true;
        try {
            yield this.client.archiveCourse(status, last_term_id, update_comment, course_rid);
            this.isSuccessCourses = true;
        } catch (error) {
            this.error = error;
            this.isError = true;
        } finally {
            this.isLoadingCourses = false;

        }
    }
    @flow
    *fetchSuggestedCourses(sectionsId?: SectionsIds) {
        this.isLoadingCourses = true;
        try {
            if(sectionsId){
                const { data } = yield this.client.getSuggestedCourses(sectionsId);
                this.suggestedCourses = data.data;
                this.isSuccessCourses = true;
                return data;
            }
            return [];
        } catch (error) {
            this.error = error;
            this.isError = true;
        } finally {
            this.isLoadingCourses = false;
        }
    }
    @flow
    *fetchUnmappedSections(page: number, name_search: string) {
        this.isLoadingCourses = true;
        try {
            const { data } = yield this.client.getUnmmapedSections(page, name_search);
            this.unmappedSections = data;
            this.isSuccessCourses = true;
            return data;
        } catch (error) {
            this.error = error;
            this.isError = true;
        } finally {
            this.isLoadingCourses = false;
        }
    }
    @flow
    *mapSectionsToCourse(sections: Array<number>, course: string) {
        this.isLoadingCourses = true;
        try {
            const { data } = yield this.client.mapSections(sections, Number(course));
            this.isSuccessMapping = true;
            return data;
        } catch (error) {
            this.error = error;
            this.isError = true;
        } finally {
            this.isLoadingCourses = false;
        }
    }
    @flow
    *createTerm(section: NewSection, onSuccess: () => void, onError?: (error: EAIError) => void) {
        this.isLoadingCourses = true;
        try {
            yield this.client.createTerm(section);
            runInAction(() => {
                this.isSuccessCourses = true;
            });
            onSuccess();
        } catch (error) {
            runInAction(() => {
                this.error = error;
                this.isError = true;
            });
            if (onError) {
                onError(error);
            }
        } finally {
            runInAction(() => {
                this.isLoadingCourses = false;
            });
        }
    }
    @flow
    *updateSectionCatalog(section: SectionRequest, rid: string) {
        try {
            yield this.client.updateSectionCatalog(section, rid);
            this.isSuccessCourses = true;
        } catch (error) {
            this.error = error;
            this.isError = true;
        } finally {
            this.isLoadingCourses = false;

        }
    }
    @flow
    *createSection(section: NewSection, onSuccess: () => void) {
        this.isLoadingCourses = true;
        try {
            // NOTE: Assuming EAIClient has a `createSection` method.
            // The original `createSecton` was calling `createTerm`.
            yield this.client.createSection(section);
            runInAction(() => {
                this.isSuccessCourses = true;
            });
            onSuccess();
        } catch (error) {
            runInAction(() => {
                this.error = error;
                this.isError = true;
            });
        } finally {
            runInAction(() => {
                this.isLoadingCourses = false;
            });
        }
    }
    @flow
    *unmapCourse(unmapping: CourseSectionUnmapping) {
        try {
            yield this.client.unmapCourse(unmapping);
            this.isSuccessMapping = true;
        } catch (error) {
            this.error = error;
            this.isError = true;
        } finally {
            this.isLoadingCourses = false;
        }
    }
    @flow
    *crossCourses(course_id: number, related_courses: number[]) {
        this.isLoadingCrossed = true;
        try {
            yield this.client.crossCourses(course_id, {related_courses});
            this.isSuccessCourses = true;
        } catch (error) {
            this.error = error;
            this.isError = true;
        } finally {
            this.isLoadingCrossed = false;
        }
    }
    @flow
    *createMapping(newMapping: CurriculumMapping) {
        this.isLoadingMapping = true;
        try {
            if (newMapping.curriculum_mapping_clos_ruuid?.trim() === "") {
                newMapping.curriculum_mapping_clos_ruuid = undefined;
            }
            // if (newMapping.cognitive_level_ruuid?.trim() === "") {
            //     newMapping.cognitive_level_ruuid = undefined;
            // }
            const { data } = yield this.client.createCurriculumMapping(newMapping);
            this.isSuccessMapping = true;
            return data;
        } catch (error) {
            this.error = error;
            this.isError = true;
        } finally {
            this.isLoadingMapping = false;

        }
    }
    @flow
    *updateCurriculumMapping(edit: CurriculumMapping, mappingId: string | number) {
        this.isLoadingMapping = true;
        try {
            if (edit.curriculum_mapping_clos_ruuid?.trim() === "") {
                edit.curriculum_mapping_clos_ruuid = undefined;
            }
            // if (edit.cognitive_level_ruuid?.trim() === "") {
            //     edit.cognitive_level_ruuid = undefined;
            // }
            const { data } = yield this.client.updateCurriculumMapping(edit, mappingId);
            this.isSuccessMapping = true;
            return data;
        } catch (error) {
            this.error = error;
            this.isError = true;
        } finally {
            this.isLoadingMapping = false;

        }
    }
    @flow
    *deleteMapping(mappingId: string) {
        this.isLoadingMapping = true;
        try {
            const { data } = yield this.client.deleteCurriculumMapping(mappingId);
            this.isSuccessMapping = true;
            return data;
        } catch (error) {
            this.error = error;
            this.isError = true;
        } finally {
            this.isLoadingMapping = false;

        }
    }
    @flow
    *createCognitiveLevel(new_cognitive: CustomOptionType) {
        this.isLoadingMapping = true;
        try {
            const { data } = yield this.client.createCognitiveLevel(new_cognitive);
            this.isSuccessMapping = true;
            return data;
        } catch (error) {
            this.error = error;
            this.isError = true;
        } finally {
            this.isLoadingMapping = false;

        }
    }
    @flow
    *fetchCognitiveLevels() {
        this.isLoadingMapping = true;
        try {
            const { data } = yield this.client.getCognitiveLevel();
            this.cognitiveLevels = data.data;
            this.isSuccessMapping = true;
            return data;
        } catch (error) {
            this.error = error;
            this.isError = true;
        } finally {
            this.isLoadingMapping = false;
        }
    }
    @flow
    *updateCognitiveLevel(edit: CustomOptionType, cognitiveId: string) {
        this.isLoadingMapping = true;
        try {
            const { data } = yield this.client.updateCognitiveLevel(edit, cognitiveId);
            this.isSuccessMapping = true;
            return data;
        } catch (error) {
            this.error = error;
            this.isError = true;
        } finally {
            this.isLoadingMapping = false;

        }
    }
    @flow
    *deleteCognitiveLevel(cognitiveId: string) {
        this.isLoadingMapping = true;
        try {
            const { data } = yield this.client.deleteCognitiveLevel(cognitiveId);
            this.isSuccessMapping = true;
            return data;
        } catch (error) {
            this.error = error;
            this.isErrorDeleteLabel = true;
            return this.isErrorDeleteLabel;
        } finally {
            this.isLoadingMapping = false;
        }
    }
    @flow
    *createCLO(new_clo: CustomOptionType) {
        this.isLoadingMapping = true;
        try {
            const { data } = yield this.client.createCLO(new_clo);
            this.isSuccessMapping = true;
            return data;
        } catch (error) {
            this.error = error;
            this.isError = true;
        } finally {
            this.isLoadingMapping = false;

        }
    }
    @flow
    *fetchCLOs() {
        this.isLoadingMapping = true;
        try {
            const { data } = yield this.client.getCLOs();
            this.CLOs = data.data;
            this.isSuccessMapping = true;
            return data;
        } catch (error) {
            this.errorMessage = error?.message || "Error to fetch CLOs";
            this.isError = true;
        } finally {
            this.isLoadingMapping = false;
        }
    }
    @flow
    *updateCLO(edit: CustomOptionType, cloId: string) {
        this.isLoadingMapping = true;
        try {
            const { data } = yield this.client.updateCLO(edit, cloId);
            this.isSuccessMapping = true;
            return data;
        } catch (error) {
            this.error = error;
            this.isError = true;
        } finally {
            this.isLoadingMapping = false;

        }
    }
    @flow
    *deleteCLO(cloId: string) {
        this.isLoadingMapping = true;
        try {
            const { data } = yield this.client.deleteCLO(cloId);
            this.isSuccessMapping = true;
            return data;
        } catch (error) {
            this.error = error;
            this.isErrorDeleteLabel = true;
            return this.isErrorDeleteLabel;
        } finally {
            this.isLoadingMapping = false;
        }
    }
    @flow
    *createDomainCompetency(new_domain: DomainCompetency) {
        this.isLoadingMapping = true;
        try {
            const { data } = yield this.client.createDomainCompetency(new_domain);
            this.isSuccessMapping = true;
            return data;
        } catch (error) {
            this.error = error;
            this.isError = true;
        } finally {
            this.isLoadingMapping = false;

        }
    }
    @flow
    *fetchDomainCompetencies() {
        this.isLoadingMapping = true;
        try {
            const { data } = yield this.client.getDomainCompetencies();
            this.domainCompetencies = data.data;
            this.isSuccessMapping = true;
            return data;
        } catch (error) {
            this.error = error;
            this.isError = true;
        } finally {
            this.isLoadingMapping = false;
        }
    }
    @flow
    *updateDomainCompetency(edit: DomainCompetency, domainId: string) {
        this.isLoadingMapping = true;
        try {
            const { data } = yield this.client.updateDomainCompetency(edit, domainId);
            this.isSuccessMapping = true;
            return data;
        } catch (error) {
            this.error = error;
            this.isError = true;
        } finally {
            this.isLoadingMapping = false;

        }
    }
    @flow
    *deleteDomainCompetency(domainId: string) {
        this.isLoadingMapping = true;
        try {
            const { data } = yield this.client.deleteDomainCompetency(domainId);
            this.isSuccessMapping = true;
            return data;
        } catch (error) {
            this.error = error;
            this.isError = true;
        } finally {
            this.isLoadingMapping = false;

        }
    }
    @flow
    *createTeachingMethod(new_teaching: CustomOptionType) {
        this.isLoadingMapping = true;
        try {
            const { data } = yield this.client.createTeachingMethod(new_teaching);
            this.isSuccessMapping = true;
            return data;
        } catch (error) {
            this.error = error;
            this.isError = true;
        } finally {
            this.isLoadingMapping = false;

        }
    }
    @flow
    *fetchTeachingMethods() {
        this.isLoadingMapping = true;
        try {
            const { data } = yield this.client.getTeachingMethods();
            this.teachingMethods = data.data;
            this.isSuccessMapping = true;
            return data;
        } catch (error) {
            this.error = error;
            this.isError = true;
        } finally {
            this.isLoadingMapping = false;
        }
    }
    @flow
    *updateTeachingMethod(edit: CustomOptionType, teachingId: string) {
        this.isLoadingMapping = true;
        try {
            const { data } = yield this.client.updateTeachingMethod(edit, teachingId);
            this.isSuccessMapping = true;
            return data;
        } catch (error) {
            this.error = error;
            this.isError = true;
        } finally {
            this.isLoadingMapping = false;

        }
    }
    @flow
    *deleteTeachingMethod(teachingId: string) {
        this.isLoadingMapping = true;
        try {
            const { data } = yield this.client.deleteTeachingMethod(teachingId);
            this.isSuccessMapping = true;
            return data;
        } catch (error) {
            this.error = error;
            this.isErrorDeleteLabel = true;
            return this.isErrorDeleteLabel;
        } finally {
            this.isLoadingMapping = false;

        }
    }
}
